/**
 * The six kitchen documents of handover rev7 §11.6 that are about one order,
 * as A4 print views: K1 and K2 — gyártási rendelés, K3 — módosítás
 * véglegesítés után, K4 — lemondás véglegesítés után, K6 — csomagolási lista
 * és címkék, and K8 — étrendi megjegyzés jóváhagyásra.
 *
 * K5 and K7 are about a day rather than an order and are `KitchenDayDoc.tsx`.
 *
 * Four rules the document holds itself to, all of them §11.6's:
 *
 *  - **From the snapshot.** Everything on the page comes out of
 *    `public.kitchen_doc`, which reads `order_lines.snapshot` and never the
 *    live catalogue (§19.2 principle 1). This component has no second source
 *    and no defaults of its own: a field the snapshot does not carry prints as
 *    a gap, in words, because §7.3's interim catalogue genuinely has no
 *    station, no lead time and no *Adag / doboz* for any dish and a document
 *    that quietly printed a zero would be lying about it.
 *  - **No prices.** There are none in the reading — the pgTAP suite asserts
 *    that over the whole document — so there are none to print.
 *  - **Allergens as code and full name**, `8 diófélék`, with §7.3's
 *    verification state beside them. Example data never reads as verified.
 *  - **ÉRVÉNYTELEN** across a superseded version, so a sheet pulled off the
 *    printer yesterday is recognisable as the wrong one.
 *
 * A4, Raleway 10 pt and page numbers are in `print/styles.ts` and
 * `print/paginate.ts`; the sheet count is the document's own, because no
 * browser engine implements the CSS that would do it.
 */

import { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { earlyPickupKitchenNotice } from '@plt/engine'

import { km } from '@plt/emails'

import { formatter } from '../../page/format.ts'
import { ALLERGENS_BEING_VERIFIED } from '../../page/model.ts'
import { consoleCall, ConsoleError } from '../api.ts'
import { statusText } from '../common.tsx'
import { useConsole } from '../context.ts'
import { paginate, pageLabel } from './paginate.ts'
import { PRINT_STYLES } from './styles.ts'
import { SiteNotice } from '../../routes/SiteNotice.tsx'

/* Rows that fit on an A4 sheet at Raleway 10 pt inside 14 mm margins: 269 mm
   of height, about 7 mm a row. The first sheet also carries the alert bands,
   the title and the header block. Conservative on purpose — a sheet that
   breaks early wastes paper; one that breaks late loses a line. */
const KITCHEN_ROWS_FIRST = 22
const KITCHEN_ROWS_REST = 32
const LABELS_FIRST = 8
const LABELS_REST = 10

export interface Allergen {
  code: number | string
  name_hu?: string | null
}

export interface DocLine {
  dish_id: string
  name_hu: string | null
  category: string | null
  price_category: string
  station: string | null
  is_gf_portion: boolean
  /** Run 17 item B5(b): the gluten-free guests' share of this line. */
  gf_portions?: number | null
  is_drink: boolean
  gluten_free_eligible: boolean | null
  ordered_portions: number
  unit: string | null
  production_units: number | null
  production_unit: string | null
  portion_definition: string | null
  portions_per_box: number | null
  production_day: string | null
  boxes: number | null
  allergens: Allergen[]
  allergen_status: string | null
  allergen_test_marked: boolean | null
}

export interface DocChange {
  dish_id: string
  name_hu: string | null
  station: string | null
  unit: string | null
  is_gf_portion: boolean | null
  was: number | null
  now: number | null
  delta: number
  change: 'added' | 'removed' | 'changed'
}

export interface KitchenDocument {
  kind: 'K1' | 'K2' | 'K3' | 'K4' | 'K6' | 'K8'
  generated_at: string
  label_consumption_window: string | null
  superseded: boolean
  version: number
  current_version: number
  bands: string[]
  /** K3 only: what moved against the version it was based on. Empty on the
   *  others, so the component reads one shape (§11.6). */
  changes: DocChange[]
  based_on_version: number | null
  version_reason: string | null
  order: {
    ref: string
    status: string
    event_name: string | null
    event_date: string
    event_start: string | null
    occasion_label: string | null
    /** Run 19 §4: the meal note the occasion carries, beside its name. */
    meal_note?: string | null
    /** Run 19 §17: *Szállításra összekészítve*, as `order_ready_by` gives it. */
    ready_by?: string | null
    ready_by_overridden?: boolean
    /** Run 19 §18: how the food leaves the kitchen, in words. */
    packing_label?: string | null
    packing_mode?: string | null
    /** Run 19 §16: the cups and the machines that go out with the food. */
    coffee_cups?: number | null
    coffee_machines?: number | null
    /** Run 19 §8: the day the food changes hands, and the collection end. */
    fulfilment_date?: string | null
    pickup_to?: string | null
    guests: number
    gf_guests: number
    fulfilment: 'delivery' | 'pickup'
    slot_label: string | null
    zone: string | null
    /** Specification §3.7: the distance beside the zone. */
    distance_km?: number | string | null
    postcode: string | null
    delivery_address: Record<string, string> | null
    customer_name: string | null
    customer_company: string | null
    customer_phone: string | null
    dietary_note: string | null
    labels_blocked: boolean
    revobox_out: number | null
    frozen_at: string | null
    /** §11.6 K2 is "a short-notice **or** late order". The kitchen has to know
     *  which: one means no slack before the event, the other that the order
     *  arrived after the window had closed. */
    is_short_notice: boolean
    is_late: boolean
    catalogue_version: string | null
  }
  lines: DocLine[]
  totals: {
    portions: number
    gf_portions: number
    drinks: number
    boxes: number
    boxes_unknown: number
  }
  dietary_signoff: {
    decision: string
    resolution: string
    signed_by: { name?: string; email?: string }
    at: string
  } | null
}

const fmt = formatter('hu')
const MISSING = '— nincs adat'

/* §5.12: the unit never wraps away from its number, the rule rev7 states for
   *Ft*. `packages/emails/src/format.ts`'s `qty()` is the same function for the
   email render of the same rows; a sheet that let *120* break from *db* across
   a line would be the one place in the system that did. */
const NBSP = '\u00a0'
const qty = (value: number, unit: string | null | undefined): string =>
  `${fmt.n(value)}${NBSP}${unit ?? 'db'}`

const asDate = (value: string | null): string =>
  value === null ? MISSING : fmt.date(new Date(`${value}T00:00:00`), true)

/**
 * §11.6: allergens always as code and full name, `8 diófélék`, with §7.3's
 * verification state beside them.
 *
 * An empty list is a **claim**: this dish contains none of the fourteen. On
 * unverified data that claim must not be made at all — §7.3 and §8 of the
 * constraints — which is why *nincs jelölt allergén* is only printed when the
 * line is Robi's own sign-off. A list that is merely unverified can carry the
 * hedge, because the reader sees what it says and that it is not signed off.
 *
 * The same rule and the same words as `packages/emails/src/kitchenDoc.ts`'s
 * `docAllergens` and `osszesites.ts`'s `allergenTextLong`. The sheet used to
 * say *nincs jelölt allergén* on example data while the email of the same
 * document said *még nincs ellenőrzött adat (példa)*.
 */
export function allergenText(line: DocLine): string {
  const parts = line.allergens.map((a) => `${a.code} ${a.name_hu ?? ''}`.trim())
  const verified = line.allergen_status === 'verified' && line.allergen_test_marked !== true
  // Only a verified dish prints a plain list, and only a verified dish with
  // an empty list prints *nincs*. Anything else opens with *Allergének
  // ellenőrzés alatt* (Barna, 10 October 2026). A kitchen document keeps
  // the unverified list after it, marked as such, because the kitchen
  // judges dietary requests by it; the customer sees the phrase alone.
  if (!verified) {
    return parts.length === 0
      ? ALLERGENS_BEING_VERIFIED.hu
      : `${ALLERGENS_BEING_VERIFIED.hu} — nem ellenőrzött lista: ${parts.join(' · ')}`
  }
  return parts.length === 0 ? 'allergének: nincs' : `allergének: ${parts.join(' · ')}`
}

/** §7.3: anything other than Robi's own sign-off prints as example data. */
export function allergenCaveat(lines: DocLine[]): string | null {
  const unverified = lines.some(
    (line) => line.allergen_status !== 'verified' || line.allergen_test_marked === true,
  )
  return unverified
    ? 'Az allergénadatok ELLENŐRZÉSRE VÁRNAK. Példaadatok, nem a konyha aláírt listája (§7.3).'
    : null
}

/**
 * §11.6 K6 puts a consumption window on every label, and rev7 does not say
 * what it is: B08 in §13 is "the drop-off handover point and consumption
 * window still need defining". A food-safety window is not something to
 * invent, so the label names the gap until `label_consumption_window` is in
 * the register — one parameter, and this is the only place that reads it.
 */
export function consumptionWindow(doc: KitchenDocument): string {
  return doc.label_consumption_window === null
    ? 'Fogyasztási idő: MEGHATÁROZANDÓ (B08) — a paraméterregiszterben még nincs érték'
    : `Fogyasztási idő: ${doc.label_consumption_window}`
}

/** One label per big box (§11.6 K6). A line with no *Adag / doboz* in the
 *  catalogue produces no label and says so: the box count is unknown, so the
 *  number of labels is unknown too. */
export interface LabelRow {
  line: DocLine
  index: number
  of: number
}

export function labelsFor(lines: DocLine[]): LabelRow[] {
  const labels: LabelRow[] = []
  for (const line of lines) {
    if (line.boxes === null || line.boxes <= 0) continue
    for (let index = 1; index <= line.boxes; index++) {
      labels.push({ line, index, of: line.boxes })
    }
  }
  return labels
}

function Bands({ doc }: { doc: KitchenDocument }) {
  return (
    <>
      {doc.superseded && (
        <div className="band void" data-testid="band-ervenytelen">
          {/* DR-20: the article is the numeral's own — *az 1.*, *a 2.*. */}
          ÉRVÉNYTELEN — ez {fmt.article(doc.version)} {doc.version}. verzió,{' '}
          {fmt.article(doc.current_version)} {doc.current_version}. verzió van érvényben
        </div>
      )}
      {doc.bands.map((band) => (
        <div className="band" key={band} data-testid={`band-${band}`}>{band}</div>
      ))}
    </>
  )
}

/**
 * Run 19 §17: an instant as the kitchen reads it — the date and the time,
 * in Europe/Budapest, whatever zone the browser printing the sheet is in
 * (§10: every date in this system is read in the event's own zone).
 */
function readyByText(value: string): string {
  const at = new Date(value)
  if (Number.isNaN(at.getTime())) return MISSING
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('hu-HU', {
      timeZone: 'Europe/Budapest',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
      .formatToParts(at)
      .map((part) => [part.type, part.value]),
  )
  return `${parts['year']}. ${parts['month']}. ${parts['day']}. ${parts['hour']}:${parts['minute']}`
}

function Header({ doc }: { doc: KitchenDocument }) {
  const o = doc.order
  /* Run 19 §8: a collection that is not on the event date. The words are
     the engine's, shared with the page, the emails and the console. */
  const pickupWarning =
    o.fulfilment === 'pickup'
      ? earlyPickupKitchenNotice('hu', {
          pickupDate: o.fulfilment_date ?? null,
          eventDate: o.event_date,
          pickupTo: o.pickup_to ?? null,
        })
      : null
  return (
    <dl className="header">
      <dt>Rendelés</dt>
      <dd data-testid="doc-ref">{o.ref}</dd>
      <dt>Verzió</dt>
      <dd>{doc.version}.</dd>
      <dt>Rendezvény</dt>
      <dd>{asDate(o.event_date)}</dd>
      <dt>Kezdés</dt>
      <dd>{o.event_start === null ? MISSING : o.event_start.slice(0, 5)}</dd>
      <dt>Teljesítés</dt>
      <dd>
        {o.fulfilment === 'pickup' ? 'Személyes átvétel' : 'Kiszállítás'}
        {o.slot_label === null ? '' : ` · ${o.slot_label}`}
        {/* *A zóna* reads as the definite article; *A jelű zóna* does not. */}
        {o.zone === null ? '' : ` · ${o.zone} jelű zóna`}
        {km(o.distance_km) === '' ? '' : ` · ${km(o.distance_km)}`}
      </dd>
      <dt>Vendégek</dt>
      <dd>
        {fmt.n(o.guests)} fő
        {o.gf_guests > 0 ? ` · ebből ${fmt.n(o.gf_guests)} gluténmentes` : ''}
      </dd>
      <dt>Alkalom</dt>
      {/* Run 19 §4: the occasion name and its meal note travel together,
          here as on every other surface. */}
      <dd data-mealnote={o.meal_note ? 'yes' : undefined}>
        {o.occasion_label ?? MISSING}
        {o.meal_note ? ` — ${o.meal_note}` : ''}
      </dd>
      {/* Run 19 §17 (EXPERT, Balázs 20261016): the time the kitchen has to
          have the order ready. A time somebody set by hand says so, so a
          reprinted sheet can be told from the one the default produced. */}
      {o.ready_by ? (
        <>
          <dt>Szállításra összekészítve</dt>
          <dd data-readyby={o.ready_by_overridden === true ? 'manual' : 'default'}>
            {readyByText(o.ready_by)}
            {o.ready_by_overridden === true ? ' · kézzel beállítva' : ''}
          </dd>
        </>
      ) : null}
      {/* Run 19 §18: how the food leaves the kitchen, which follows the
          staff answer on every occasion. */}
      {o.packing_label ? (
        <>
          <dt>Csomagolás</dt>
          <dd data-packing={o.packing_mode ?? undefined}>{o.packing_label}</dd>
        </>
      ) : null}
      {/* Run 19 §16: the cups and the machines. */}
      {typeof o.coffee_cups === 'number' && o.coffee_cups > 0 ? (
        <>
          <dt>Kávé</dt>
          <dd data-coffee="yes">
            {fmt.n(o.coffee_cups)} csésze
            {typeof o.coffee_machines === 'number' && o.coffee_machines > 0
              ? ` · ${fmt.n(o.coffee_machines)} kávégép`
              : ''}
          </dd>
        </>
      ) : null}
      {/* Run 19 §8: a collection that is not on the event date names the
          **production deadline**, so the event date cannot be mistaken
          for it. */}
      {pickupWarning === null ? null : (
        <>
          <dt>Figyelem</dt>
          <dd data-pickupalert="early">{pickupWarning}</dd>
        </>
      )}
      <dt>Állapot</dt>
      {/* §11.1's Internal (HU) column. The kitchen reads *véglegesítve*, and
          never *fagyasztva* — rev7 says so in the status table itself. */}
      <dd>{statusText(o.status)}</dd>
    </dl>
  )
}

function ProductionRows({ rows }: { rows: DocLine[] }) {
  return (
    <table>
      <thead>
        <tr>
          <th>Fogás</th>
          <th>Állomás</th>
          <th className="num">Adag</th>
          <th className="num">Gyártási egység</th>
          <th>Gyártási nap</th>
          <th>Gluténmentes</th>
          <th>Allergének</th>
        </tr>
      </thead>
      <tbody>
        {/* Run 17 item B5(b), assumption A5: where a dish on the menu
            carries the gluten-free guests' share, the kitchen reads the
            share as its own packing line and the dish's own line is what is
            left. One batch, two boxes. */}
        {rows.flatMap((line, index) => {
          const share = line.is_gf_portion ? 0 : Math.max(line.gf_portions ?? 0, 0)
          const rest = Math.max(line.ordered_portions - share, 0)
          const row = (portions: number, glutenFree: boolean, key: string): JSX.Element => (
            <tr key={key} data-testid={`k1-line-${line.dish_id}`} data-gfrow={glutenFree ? '1' : undefined}>
              <td>{line.name_hu ?? line.dish_id}</td>
              <td className={line.station === null ? 'gap' : undefined}>{line.station ?? MISSING}</td>
              <td className="num">{qty(portions, line.unit)}</td>
              <td className={`num${!glutenFree && line.production_units === null ? ' gap' : ''}`}>
                {glutenFree
                  ? '—'
                  : line.production_units === null
                    ? MISSING
                    : `${fmt.n(line.production_units)} ${line.production_unit ?? ''}`.trim()}
              </td>
              <td className={line.production_day === null ? 'gap' : undefined}>
                {line.production_day === null ? MISSING : asDate(line.production_day)}
              </td>
              <td className={glutenFree ? 'gf' : undefined}>
                {glutenFree
                  ? 'GLUTÉNMENTES — külön doboz'
                  : line.gluten_free_eligible === true
                    ? 'alkalmas'
                    : '—'}
              </td>
              <td className="allergens">{allergenText(line)}</td>
            </tr>
          )
          const key = `${line.dish_id}-${index}`
          if (share <= 0) return [row(line.ordered_portions, line.is_gf_portion, key)]
          if (rest <= 0) return [row(share, true, key)]
          return [row(rest, false, key), row(share, true, `${key}-gf`)]
        })}
      </tbody>
    </table>
  )
}

const CHANGE_LABEL: Record<string, string> = {
  added: 'új fogás',
  removed: 'elmarad',
  changed: 'megváltozott',
}

/**
 * §11.6 K3, *modification after freeze*. The kitchen is holding a sheet that
 * is now wrong, and the thing it needs first is what moved — not the new order
 * repeated, which is below. A dish on one version only is an addition or a
 * removal; one on both carries the delta.
 */
function ChangeRows({ rows }: { rows: DocChange[] }) {
  if (rows.length === 0) {
    return (
      <p className="gap" data-testid="k3-no-changes">
        A tételek nem változtak; a módosítás a rendelés más adatát érintette.
      </p>
    )
  }
  return (
    <table data-testid="k3-changes">
      <thead>
        <tr>
          {/* *Korábban · Most*, not *Eddig · Mostantól*: the day view already
              uses *Eddig* for the end of a date range. */}
          <th>Fogás</th>
          <th>Változás</th>
          <th className="num">Korábban</th>
          <th className="num">Most</th>
          <th className="num">Különbség</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((change, index) => (
          <tr key={`${change.dish_id}-${index}`} data-testid={`k3-change-${change.dish_id}`}>
            <td>
              {change.name_hu ?? change.dish_id}
              {change.is_gf_portion === true ? ' — gluténmentes, külön doboz' : ''}
            </td>
            <td>{CHANGE_LABEL[change.change] ?? change.change}</td>
            <td className="num">{change.was === null ? '—' : qty(change.was, change.unit)}</td>
            <td className="num">{change.now === null ? '—' : qty(change.now, change.unit)}</td>
            <td className="num gf">
              {change.delta > 0 ? '+' : ''}{qty(change.delta, change.unit)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function PackingRows({ rows }: { rows: DocLine[] }) {
  return (
    <table>
      <thead>
        <tr>
          <th>Fogás</th>
          <th className="num">Adag</th>
          <th className="num">Adag / doboz</th>
          <th className="num">Doboz</th>
          <th>Gluténmentes</th>
          <th>Allergének</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((line, index) => (
          <tr key={`${line.dish_id}-${index}`} data-testid={`k6-line-${line.dish_id}`}>
            <td>{line.name_hu ?? line.dish_id}</td>
            <td className="num">{qty(line.ordered_portions, line.unit)}</td>
            <td className={`num${line.portions_per_box === null ? ' gap' : ''}`}>
              {line.portions_per_box === null ? MISSING : fmt.n(line.portions_per_box)}
            </td>
            <td className={`num${line.boxes === null ? ' gap' : ''}`}>
              {line.boxes === null ? MISSING : fmt.n(line.boxes)}
            </td>
            <td className={line.is_gf_portion ? 'gf' : undefined}>
              {line.is_gf_portion ? 'KÜLÖN DOBOZBAN' : '—'}
            </td>
            <td className="allergens">{allergenText(line)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function Foot({ doc, page, pages }: { doc: KitchenDocument; page: number; pages: number }) {
  return (
    <div className="foot">
      <span>{doc.kind} · {doc.order.ref} · {doc.version}. verzió</span>
      <span>Planteen · {fmt.date(new Date(doc.generated_at), false)}</span>
      <span className="page" data-testid={`page-${page}`}>{pageLabel(page, pages)}</span>
    </div>
  )
}

const TITLE: Record<string, string> = {
  K1: 'Gyártási rendelés',
  K2: 'Gyártási rendelés — soron kívül',
  K3: 'Módosítás véglegesítés után',
  K4: 'Lemondás véglegesítés után',
  K6: 'Csomagolási lista és címkék',
  K8: 'Étrendi megjegyzés jóváhagyásra',
}

/**
 * §11.6 K2 is "a short-notice **or** late order", and the two are different
 * facts about the order. The band says which one is true; this says what it
 * means, so nobody has to know the lane names to read the sheet.
 */
export function k2Reason(doc: KitchenDocument): string {
  const { is_short_notice: short, is_late: late } = doc.order
  // §11.2's row and §4's "Inside the freeze" make the two alternatives and
  // say which wins: a short-notice order is marked RÖVID HATÁRIDŐ "(not
  // KÉSŐI RENDELÉS)". It is late by construction, so the sentence does not
  // claim both.
  if (short) return 'Rövid határidős sávban érkezett: a rendezvényig kevesebb idő van a szokásosnál.'
  if (late) return 'A visszaigazolása a véglegesítési határidő után érkezett.'
  return 'Soron kívüli gyártási rendelés.'
}

export function KitchenDocView(): JSX.Element {
  const { orderId = '', kind = 'K1' } = useParams()
  const [search] = useSearchParams()
  const { config, session } = useConsole()
  const [doc, setDoc] = useState<KitchenDocument | null>(null)
  const [error, setError] = useState<string | null>(null)

  const version = search.get('verzio')

  useEffect(() => {
    let live = true
    consoleCall<KitchenDocument>(
      'kitchen_doc',
      { order_id: orderId, kind, ...(version === null ? {} : { version: Number(version) }) },
      { config, session },
    )
      .then((result) => live && setDoc(result))
      .catch((cause: unknown) =>
        live && setError(cause instanceof ConsoleError ? cause.message : 'Nem sikerült betölteni.')
      )
    return () => {
      live = false
    }
  }, [config, session, orderId, kind, version])

  const paged = useMemo(() => {
    if (doc === null) return null
    if (doc.kind === 'K6') {
      // §11.6: labels do not print while `labels_blocked` is set.
      const labels = doc.order.labels_blocked ? [] : labelsFor(doc.lines)
      return {
        lines: paginate(doc.lines, KITCHEN_ROWS_FIRST, KITCHEN_ROWS_REST),
        labels: paginate(labels, LABELS_FIRST, LABELS_REST),
      }
    }
    return { lines: paginate(doc.lines, KITCHEN_ROWS_FIRST, KITCHEN_ROWS_REST), labels: null }
  }, [doc])

  if (error !== null) {
    return (
      <div className="plt-print" data-testid="kitchen-doc-error">
        <style>{PRINT_STYLES}</style>
        <div className="sheet"><p>{error}</p></div>
      </div>
    )
  }
  if (doc === null || paged === null) {
    return (
      <div className="plt-print">
        <style>{PRINT_STYLES}</style>
        <div className="sheet"><p>Betöltés…</p></div>
      </div>
    )
  }

  const title = TITLE[doc.kind] ?? doc.kind
  const caveat = allergenCaveat(doc.lines)
  const labelPages = paged.labels?.pages ?? 0
  const pages = paged.lines.pages + labelPages

  return (
    <div className="plt-print" data-testid={`kitchen-doc-${doc.kind}`}>
      <style>{PRINT_STYLES}</style>
      <SiteNotice />

      <div className="toolbar">
        <button type="button" onClick={() => globalThis.print?.()} data-testid="print">
          Nyomtatás
        </button>
        <span>A4 · Raleway 10 pt · {pages} oldal</span>
        <span className="spacer">
          {doc.order.ref} · {doc.kind} · {doc.version}. verzió
        </span>
      </div>

      {paged.lines.sheets.map((sheet) => (
        <section className="sheet" key={`lines-${sheet.page}`} data-testid={`sheet-${sheet.page}`}>
          {sheet.page === 1 && (
            <>
              <Bands doc={doc} />
              <h1>{title}</h1>
              <p className="sub">
                {doc.order.event_name ?? 'Rendezvény'} · {doc.order.customer_company ??
                  doc.order.customer_name ?? ''}
              </p>
              <Header doc={doc} />
            </>
          )}

          {sheet.page === 1 && doc.kind === 'K2' && (
            <p className="lead" data-testid="k2-reason">{k2Reason(doc)}</p>
          )}

          {sheet.page === 1 && doc.kind === 'K3' && (
            <>
              <p className="lead">
                Ez a rendelés a véglegesítés után módosult. A korábbi gyártási rendelés
                ÉRVÉNYTELEN; ez a változat érvényes.
              </p>
              {doc.based_on_version !== null && (
                <p className="sub" data-testid="k3-based-on">
                  {/* DR-20 again: *az 1. verziót*, *a 2. verziót*. */}
                  Felváltja {fmt.article(doc.based_on_version)} {doc.based_on_version}. verziót
                  {doc.version_reason === null ? '' : ` · ${doc.version_reason}`}
                </p>
              )}
              <h2>Ami változott</h2>
              <ChangeRows rows={doc.changes} />
              <h2>A teljes gyártási rendelés</h2>
            </>
          )}

          {sheet.page === 1 && doc.kind === 'K4' && (
            <p className="lead" data-testid="k4-stop">
              Ezt a rendelést ne gyártsák le. A korábbi gyártási rendelés ÉRVÉNYTELEN.
              Az alábbi tételek maradnak el.
            </p>
          )}

          {sheet.page === 1 && doc.kind === 'K8' && (
            <div className="block" data-testid="k8-note">
              <h2>A vendég megjegyzése</h2>
              <p>{doc.order.dietary_note ?? 'HIÁNYZÓ ADAT: a megjegyzés szövege nem olvasható.'}</p>
            </div>
          )}

          {doc.kind === 'K6'
            ? <PackingRows rows={sheet.rows} />
            : <ProductionRows rows={sheet.rows} />}

          {sheet.page === paged.lines.pages && (
            <>
              <div className="totals" data-testid="totals">
                Összesen {fmt.n(doc.totals.portions)} adag
                {doc.totals.gf_portions > 0
                  ? ` · ebből ${fmt.n(doc.totals.gf_portions)} gluténmentes, külön dobozban`
                  : ''}
                {doc.totals.drinks > 0 ? ` · ebből ${fmt.n(doc.totals.drinks)} ital` : ''}
                {doc.kind === 'K6' ? ` · ${fmt.n(doc.totals.boxes)} doboz` : ''}
                {doc.kind === 'K6' && doc.order.revobox_out !== null
                  ? ` · RevoBox kivitel ${fmt.n(doc.order.revobox_out)}`
                  : ''}
              </div>

              {doc.totals.boxes_unknown > 0 && doc.kind === 'K6' && (
                <div className="block" data-testid="boxes-gap">
                  <h2>Hiányzó adat</h2>
                  <p>
                    {fmt.n(doc.totals.boxes_unknown)} fogáshoz nincs <em>Adag / doboz</em> a
                    katalógusban, ezért a dobozszám és a hozzá tartozó címke nem számolható ki.
                    A hiányt a katalógusban kell pótolni (§7.3).
                  </p>
                </div>
              )}

              {caveat !== null && (
                <div className="block" data-testid="allergen-caveat"><p>{caveat}</p></div>
              )}

              {doc.kind === 'K8' && (
                <div className="block" data-testid="k8-gate">
                  <p>A felülvizsgálat az aláírásig nem zárható le (§11.4).</p>
                </div>
              )}

              {doc.order.dietary_note !== null && (
                <div className="block" data-testid="dietary-note">
                  <h2>Étrendi megjegyzés</h2>
                  <p>{doc.order.dietary_note}</p>
                  {doc.dietary_signoff === null
                    ? (
                      <p className="gap">
                        Aláírás még nincs. A konyhai dokumentum az allergén aláíró döntése nélkül
                        nem teljes (§11.4).
                      </p>
                    )
                    : (
                      <p>
                        <strong>
                          {doc.dietary_signoff.decision === 'signed' ? 'Jóváhagyva' : 'Elutasítva'}
                        </strong>
                        {' — '}
                        {doc.dietary_signoff.resolution}
                        {' · '}
                        {doc.dietary_signoff.signed_by?.name ?? ''}
                        {' · '}
                        {fmt.date(new Date(doc.dietary_signoff.at), false)}
                      </p>
                    )}
                </div>
              )}

              <div className="block" data-testid="delivery-block">
                <h2>{doc.order.fulfilment === 'pickup' ? 'Átvétel' : 'Kiszállítás'}</h2>
                <p>
                  {doc.order.fulfilment === 'pickup'
                    ? 'Személyes átvétel a Planteen konyhájában.'
                    : [
                      doc.order.delivery_address?.postcode,
                      doc.order.delivery_address?.town,
                      doc.order.delivery_address?.street_and_number,
                      doc.order.delivery_address?.floor_and_door,
                    ].filter(Boolean).join(' ') || MISSING}
                </p>
                <p>
                  {doc.order.slot_label ?? MISSING}
                  {doc.order.customer_phone === null ? '' : ` · ${doc.order.customer_phone}`}
                </p>
              </div>
            </>
          )}

          <Foot doc={doc} page={sheet.page} pages={pages} />
        </section>
      ))}

      {doc.kind === 'K6' && doc.order.labels_blocked && (
        <section className="sheet" data-testid="labels-blocked">
          <div className="band">CÍMKÉZÉS BLOKKOLVA</div>
          <h1>Címkék</h1>
          <p>
            Ehhez a rendeléshez nem nyomtatható címke, amíg a címkézés blokkolva van (§11.6).
          </p>
          <Foot doc={doc} page={paged.lines.pages + 1} pages={pages} />
        </section>
      )}

      {doc.kind === 'K6' && !doc.order.labels_blocked &&
        paged.labels?.sheets.map((sheet) => (
          <section
            className="sheet"
            key={`labels-${sheet.page}`}
            data-testid={`label-sheet-${sheet.page}`}
          >
            {sheet.page === 1 && (
              <>
                <h1>Címkék</h1>
                <p className="sub">
                  Dobozonként egy címke. {doc.order.ref} · {asDate(doc.order.event_date)}
                </p>
              </>
            )}
            <div className="label-sheet">
              {sheet.rows.map((label, index) => (
                <div className="label" key={`${label.line.dish_id}-${label.index}-${index}`}>
                  <div className="ref">{doc.order.ref} · {asDate(doc.order.event_date)}</div>
                  <div className="dish">{label.line.name_hu ?? label.line.dish_id}</div>
                  {label.line.is_gf_portion && <div className="gf">GLUTÉNMENTES</div>}
                  <div className="al">{allergenText(label.line)}</div>
                  <div className="al">
                    {label.index}/{label.of} doboz
                    {label.line.portions_per_box === null
                      ? ''
                      : ` · ${fmt.n(label.line.portions_per_box)} adag`}
                  </div>
                  <div className={`al${doc.label_consumption_window === null ? ' gap' : ''}`}>
                    {consumptionWindow(doc)}
                  </div>
                </div>
              ))}
            </div>
            <Foot doc={doc} page={paged.lines.pages + sheet.page} pages={pages} />
          </section>
        ))}
    </div>
  )
}
